import { computeContentHash } from "../computeContentHash";
import { isStepTestPath } from "../stepConfig";
import { STEP_TEST_CASES_DIR } from "./constants";
import { STEP_TEST_REQUESTS_SUFFIX } from "./requests";
import { isStepTestVolumeRelPathForName } from "./volumes";

type FileWithHash = { readonly path: string; readonly blobHash: string };

/**
 * Hash of a step's content files, excluding its test files — mirrors the
 * server's `getStepContentHash` exactly so a value computed here in the client
 * compares equal to the `codeHash` the step-executions route reports.
 */
export function stepContentHashFromFiles(
  files: readonly FileWithHash[],
  stepDir: string
): Promise<string> {
  const prefix = `${stepDir}/`;
  const stepFiles = files.filter(
    (f) => f.path.startsWith(prefix) && !isStepTestPath(f.path)
  );
  if (stepFiles.length === 0) return Promise.resolve("");
  return computeContentHash(
    stepFiles.map((f) => ({ path: f.path, blobHash: f.blobHash }))
  );
}

/**
 * Hash of one step test's input, expected output, and volume fixtures. A run
 * records this per spec so the client can invalidate the result when the test's
 * definition changes, even though the step's `codeHash` deliberately ignores
 * test files.
 */
export function testIoHashFromFiles(
  files: readonly FileWithHash[],
  stepDir: string,
  name: string
): Promise<string> {
  const base = `${stepDir}/${STEP_TEST_CASES_DIR}/${name}`;
  const stepPrefix = `${stepDir}/`;
  const io = files.filter(
    (f) =>
      f.path === `${base}.input` ||
      f.path === `${base}.output` ||
      f.path === `${base}${STEP_TEST_REQUESTS_SUFFIX}` ||
      (f.path.startsWith(stepPrefix) &&
        isStepTestVolumeRelPathForName(f.path.slice(stepPrefix.length), name))
  );
  return computeContentHash(
    io.map((f) => ({ path: f.path, blobHash: f.blobHash }))
  );
}

export type StepTestDotStatus =
  | "notrun"
  | "running"
  | "passed"
  | "failed"
  | "skipped";

export type StepTestRow = {
  name: string;
  status: StepTestDotStatus;
  message: string | null;
};

type SpecLike = {
  readonly name: string;
  readonly status: "passed" | "failed" | "skipped";
  readonly message: string | null;
  readonly inputOutputHash: string | null;
};

type ExecutionLike = {
  readonly status: "pending" | "success" | "error";
  readonly codeHash: string | null;
  readonly testSpecs: readonly SpecLike[];
};

/**
 * Resolve the dot status of every named step test against the run history.
 *
 * A test is only as fresh as the run it points to: we take the newest
 * execution whose `codeHash` still matches the step's current code, then
 * show as not run any test whose recorded input/output hash no longer matches
 * (edited) or that the run never covered (newly pinned). Editing the step's
 * code changes the code hash, so no execution matches and every test is
 * invalidated.
 */
export function computeStepTestRows({
  testNames,
  codeHash,
  ioHashByName,
  executions,
}: {
  testNames: readonly string[];
  codeHash: string;
  ioHashByName: ReadonlyMap<string, string>;
  executions: readonly ExecutionLike[];
}): { rows: StepTestRow[]; latest: ExecutionLike | null } {
  const latest =
    executions.find((e) => (e.codeHash ?? "") === codeHash) ?? null;
  const running = latest?.status === "pending";
  const rows = testNames.map<StepTestRow>((name) => {
    if (!latest) return { name, status: "notrun", message: null };
    if (running) return { name, status: "running", message: null };
    const spec = latest.testSpecs.find((s) => s.name === name);
    if (!spec) return { name, status: "notrun", message: null };
    if ((spec.inputOutputHash ?? "") !== (ioHashByName.get(name) ?? ""))
      return { name, status: "notrun", message: null };
    return { name, status: spec.status, message: spec.message };
  });
  return { rows, latest };
}

/** Single dot summarizing a step's tests, for the step row in the Testing tab. */
export function rollupStepTestStatus(
  rows: readonly StepTestRow[]
): StepTestDotStatus {
  if (rows.some((r) => r.status === "running")) return "running";
  if (rows.some((r) => r.status === "failed")) return "failed";
  if (rows.some((r) => r.status === "notrun")) return "notrun";
  if (rows.length === 0) return "notrun";
  return "passed";
}
